import { useEffect, useState } from "react";
import { Building2, UserRound, Save, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

export function MetricsPanel() {
  const { t } = useI18n();
  const [m, setM] = useState(null);
  const [cb, setCb] = useState("");
  const [pb, setPb] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/metrics").then(({ data }) => {
    setM(data);
    setCb(String(data.companies_baseline));
    setPb(String(data.professionals_baseline));
  }).catch(() => setM(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/metrics/baseline", {
        companies_baseline: Number(cb) || 0,
        professionals_baseline: Number(pb) || 0,
      });
      toast.success(t("dash.kf_saved"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!m) return null;

  const Big = ({ icon: Icon, value, label, baseline, added, accent, testid }) => (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d14] p-6" data-testid={testid}>
      <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center" style={{ background: accent }}>
        <Icon className="w-5 h-5 text-white" />
      </span>
      <p className="mt-4 font-display font-black text-4xl text-white tabular-nums">{value.toLocaleString("fr-FR")}</p>
      <p className="text-sm text-zinc-400">{label}</p>
      <p className="mt-2 text-xs text-zinc-500">{baseline.toLocaleString("fr-FR")} + {added.toLocaleString("fr-FR")} {t("dash.kf_added")}</p>
    </div>
  );

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d14] p-6" data-testid="admin-metrics-panel">
      <h3 className="font-display font-semibold text-white">{t("dash.kf_title")}</h3>
      <p className="text-xs text-zinc-500 mt-1">{t("dash.kf_calc")}</p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Big icon={Building2} value={m.totals.companies_supported} label={t("dash.kf_companies")}
             baseline={m.companies_baseline} added={m.companies_added} accent="#06b6d4" testid="admin-kf-companies" />
        <Big icon={UserRound} value={m.totals.professionals_equipped} label={t("dash.kf_pros")}
             baseline={m.professionals_baseline} added={m.professionals_added} accent="#8b5cf6" testid="admin-kf-professionals" />
      </div>

      <div className="mt-6 rounded-xl bg-white/5 p-4">
        <p className="text-sm text-white font-medium">{t("dash.kf_baseline")}</p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <label className="text-xs text-zinc-400">
            {t("dash.kf_companies")}
            <input type="number" min="0" value={cb} onChange={(e) => setCb(e.target.value)}
                   data-testid="admin-kf-companies-input"
                   className="mt-1 w-full rounded-lg bg-[#0a0a12] border border-white/10 px-3 py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-zinc-400">
            {t("dash.kf_pros")}
            <input type="number" min="0" value={pb} onChange={(e) => setPb(e.target.value)}
                   data-testid="admin-kf-pros-input"
                   className="mt-1 w-full rounded-lg bg-[#0a0a12] border border-white/10 px-3 py-2 text-white text-sm" />
          </label>
          <button onClick={save} disabled={saving} data-testid="admin-kf-save-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-gradient text-white text-sm font-semibold px-4 py-2.5 disabled:opacity-60">
            <Save className="w-4 h-4" /> {t("dash.kf_save")}
          </button>
        </div>
      </div>

      <div className="mt-6">
        <p className="text-sm text-white font-medium flex items-center gap-2"><History className="w-4 h-4 text-cyan-400" /> {t("dash.kf_events")}</p>
        <div className="mt-3 space-y-2 max-h-64 overflow-y-auto" data-testid="admin-kf-events">
          {(m.events || []).length === 0 && <p className="text-sm text-zinc-500">{t("dash.kf_noEvents")}</p>}
          {(m.events || []).map((ev) => (
            <div key={ev.id} className="flex items-center justify-between text-sm rounded-lg bg-white/5 px-3 py-2">
              <div className="min-w-0">
                <span className="text-white truncate">{ev.label || (ev.kind === "company" ? t("dash.kf_companies") : t("dash.kf_pros"))}</span>
                <span className="ml-2 text-xs text-zinc-500">{(ev.created_at || "").slice(0, 10)}</span>
              </div>
              <span className={`shrink-0 font-mono text-xs font-semibold ${ev.delta > 0 ? "text-cyan-400" : "text-zinc-500"}`}>
                {ev.kind === "adjustment" ? "±" : `+${ev.delta} `}
                {ev.kind === "company" ? "🏢" : ev.kind === "professional" ? "👤" : ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
